'use client';

import { motion } from 'framer-motion';
import { stegaClean } from '@sanity/client/stega';

import { PageBlogPostSchema } from '@/sanity/pages/PageBlogPostTemplate';
import { IBlogPostCard } from '@/sanity/lib/definitions';
import { DEFAULT_LANGUAGE } from '@/sanity/globals/I18n';
import { useIsMobile } from '@/hooks/useIsMobile';
import DesignScaleLock from '../global/DesignScaleLock';
import Footer from '../global/partials/Footer';
import HeaderV2 from '../global/partials/HeaderV2';
import ScrollingBanner from '../pageComponents/global/ScrollingBanner';
import BlogPostHero from '../pageComponents/blog/BlogPostHero';
import BlogShareRail from '../pageComponents/blog/BlogShareRail';
import BlogRelatedPosts from '../pageComponents/blog/BlogRelatedPosts';
import { BlogPortableText } from '../pageComponents/blog/blogPortableText';

const ease = [0.25, 0.1, 0.25, 1] as const;

function pickRelated(data: PageBlogPostSchema): IBlogPostCard[] {
  const seen = new Set<string>();
  return [...(data.relatedSameCategory ?? []), ...(data.relatedLatest ?? [])]
    .filter((post) => {
      if (!post?._id || seen.has(post._id)) return false;
      seen.add(post._id);
      return true;
    })
    .slice(0, 3);
}

export default function PageBlogPostJSX({ data }: { data: PageBlogPostSchema }) {
  const language = stegaClean(data.settings?.language) || DEFAULT_LANGUAGE;
  const related = pickRelated(data);
  const isMobile = useIsMobile();
  const hasBody = !!data.body?.length;

  return (
    <main className="flex flex-col items-center bg-white">
      <DesignScaleLock />
      {data?.settings?.header && (
        <HeaderV2
          data={data.settings.header}
          headerStyle="light"
          headerType="main"
          language={data.settings?.language}
        />
      )}

      <BlogPostHero data={data} language={language} />

      {hasBody && (
        <section className="w-full max-lg:pb-[7rem] max-lg:pt-[4rem] lg:pb-[18.625rem] lg:pt-[11.25rem]">
          <div className="relative mx-auto max-lg:w-[min(94.25rem,calc(100%-4rem))] lg:w-[94.25rem]">
            <BlogShareRail
              title={data.title}
              footer={data.settings?.footer}
              header={data.settings?.header}
              language={language}
              className="absolute right-full top-0 mr-[7rem] h-full max-lg:hidden lg:block"
            />

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease }}
            >
              <BlogPortableText value={data.body} language={language} />
            </motion.div>
          </div>
        </section>
      )}

      <BlogRelatedPosts
        posts={related}
        title={data.blog?.relatedTitle}
        language={language}
        className={hasBody ? undefined : 'max-lg:pt-[4rem] lg:pt-[18.625rem]'}
      />

      <ScrollingBanner
        className="border-y-0 bg-[#f4f5f5] max-lg:h-[7.125rem] lg:h-[10rem]"
        words={data.blog?.scrollingWords?.words}
        wordsI18n={data.blog?.scrollingWords?.wordsI18n}
        wordClassName="font-bold lg:text-[2.25rem] lg:tracking-[0.045rem]"
        separatorClassName="rounded-none bg-black max-lg:!h-[0.375rem] max-lg:!w-[0.375rem]"
        gapRem={isMobile ? 4.75 : 6.625}
      />

      {data?.settings?.footer && (
        <Footer data={data.settings.footer} language={data.settings?.language} />
      )}
    </main>
  );
}
